import clsx from "clsx";
import { Command } from "cmdk";
import { Icon } from "../../icon/Icon";
import * as selectStyles from "../common/Select.css";
import { Option, SelectSize } from "../common/SelectBase";

export interface SingleSelectListProps {
  /**
   *  Array of selectable item values
   */
  options: Option[];
  /**
   *  Callback when an item is selected
   */
  onOptionSelect: (option: Option) => void;
  /**
   * Currently selected option
   */
  selectedOption?: Option;
  /**
   * The size variant of the select.
   */
  size?: SelectSize;
  /**
   * Index of the keyboard-active row (highlighted via Arrow keys).
   */
  activeIndex?: number;
  /**
   * `id` of the option list — namespaces each option's `id` for
   * `aria-activedescendant`.
   */
  listId?: string;
  /**
   * The creatable row (`allowCreate`): the text the user typed, offered as a
   * new value. Rendered last, after the matching options.
   */
  createOption?: Option;
  /**
   * `id` of the creatable row, for `aria-activedescendant`.
   */
  createOptionId?: string;
}

export function SingleSelectOptions({
  options,
  selectedOption,
  onOptionSelect,
  size = "sm",
  activeIndex,
  listId,
  createOption,
  createOptionId
}: SingleSelectListProps): React.ReactElement {
  return (
    <>
      {options.map((option, index) => {
        const isSelected = selectedOption?.value === option.value;
        const hasDescription = !!option.description;
        return (
          <Command.Item
            key={option.value}
            id={listId ? `${listId}-${option.value}` : undefined}
            value={option.value}
            data-index={index}
            data-active={index === activeIndex ? "true" : undefined}
            className={clsx(
              selectStyles.selectItem,
              !hasDescription && selectStyles.selectItemSizes[size],
              hasDescription && selectStyles.selectItemWithDescription,
              isSelected && selectStyles.selectItemSelected
            )}
            onSelect={() => onOptionSelect(option)}
            aria-selected={isSelected}
            role="option"
            tabIndex={-1}
          >
            {hasDescription ? (
              <div className={selectStyles.selectItemTextStack}>
                <span className={selectStyles.selectItemLabel}>
                  {option.label}
                </span>
                <span className={selectStyles.selectItemDescription}>
                  {option.description}
                </span>
              </div>
            ) : (
              option.label
            )}
          </Command.Item>
        );
      })}
      {createOption && (
        <Command.Item
          id={createOptionId}
          value={createOption.value}
          data-index={options.length}
          data-active={options.length === activeIndex ? "true" : undefined}
          data-slot="create-option"
          className={clsx(
            selectStyles.selectItem,
            selectStyles.selectItemSizes[size],
            selectStyles.selectItemCreate
          )}
          onSelect={() => onOptionSelect(createOption)}
          // Named explicitly so the icon's own label stays out of the
          // announcement.
          aria-label={createOption.label}
          aria-selected={false}
          role="option"
          tabIndex={-1}
        >
          <Icon name="add" />
          {createOption.label}
        </Command.Item>
      )}
    </>
  );
}
